import { useMemo } from 'react';
import { useAssetsExchangeRates } from '@/features/exchange/api/queries';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import {
  applyAmountPrecision,
  formatReceivedAmount,
  formatReceivedPrice,
} from '@/shared/lib';

export const useInternalTransferExchangeRate = (
  assetCodeFrom: string,
  assetCodeTo: string,
  amountFrom: number
) => {
  const { t } = useTranslation();

  const { data: assetsExchangeRatesData } = useAssetsExchangeRates(
    assetCodeFrom,
    assetCodeFrom === assetCodeTo ? '' : assetCodeTo
  );

  const { exchangeRateDescription, secondData } = useMemo(() => {
    const defaultData = { exchangeRateDescription: '', secondData: [] };
    if (
      !assetsExchangeRatesData ||
      !assetCodeFrom ||
      !assetCodeTo ||
      assetCodeFrom === assetCodeTo
    ) {
      return defaultData;
    }

    const {
      priceBid,
      priceAsk,
      displayRateAsk,
      displayRateBid,
      assetLeft,
      assetRight,
    } = assetsExchangeRatesData;

    const isDirect =
      assetLeft.code === assetCodeFrom && assetRight.code === assetCodeTo;
    const isReverse =
      assetLeft.code === assetCodeTo && assetRight.code === assetCodeFrom;

    if (!isDirect && !isReverse) return defaultData;

    const exchangeRate = isDirect ? priceBid : priceAsk;
    const displayExchangeRate = isDirect
      ? (displayRateBid ?? priceBid)
      : (displayRateAsk ?? priceAsk);

    const assetToReceive = isDirect ? assetRight : assetLeft;
    const assetToPay = isDirect ? assetLeft : assetRight;

    if (!exchangeRate) {
      return defaultData;
    }

    const exchangeRateDescription = `1 ${isDirect ? assetToPay.code : assetToReceive.code} ≈ ${formatReceivedPrice(displayExchangeRate)} ${isDirect ? assetToReceive.code : assetToPay.code}`;

    const secondData = [
      {
        key: t('exchange.youPay'),
        value: `${formatReceivedAmount(amountFrom)} ${assetCodeFrom}`.trim(),
      },
      {
        key: t('exchange.exchangeRate'),
        value: exchangeRateDescription,
      },
      {
        key: t('exchange.youReceive'),
        value: `≈ ${applyAmountPrecision(
          isDirect
            ? (amountFrom || 0) * exchangeRate
            : (amountFrom || 0) / exchangeRate,
          assetToReceive.precision ?? NUMERIC_CONFIG.precision.default,
          'credit'
        )} ${assetCodeTo}`,
      },
    ];

    return { exchangeRateDescription, secondData };
  }, [assetsExchangeRatesData, assetCodeFrom, assetCodeTo, amountFrom]);

  return { exchangeRateDescription, secondData };
};
